// frontend/src/components/FinanceDashboard.js
//
// Finance-admin "My Dashboard". Finance admins have no ns_rep_id, so instead of a
// personal rep view they get a company-wide picture for the selected quarter:
//
//   1. Closed Won to Goal — booked GP (closed-won by actual close date, via
//      /api/payouts/team-bookings) vs the finance-configured team_view_quota_gp.
//   2. Shipped GP by Category — company-wide shipped GP (fulfillment basis) by
//      category, via /api/payouts/team-shipped-by-category.
//   3. Pending Shipment — All Reps — held-back opportunities awaiting fulfillment,
//      via /api/admin/pending (current/open state — not quarter-filtered).
//
// Booked GP (card 1) and shipped GP (card 2) are deliberately different bases and
// will not match: bookings are by close date, shipments by fulfillment date.
import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { getTeamBookings, getQuotas, getTeamShippedByCategory, getPendingAll } from '../api/client';

const fmt = (n) => n == null ? '—' : new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(n);

// Whole-dollar formatting for large GP figures (no cents)
const fmt0 = (n) => n == null ? '—' : new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
}).format(n);

const pct = (n, digits = 1) => `${(n || 0).toFixed(digits)}%`;

function getCurrentQuarter() {
  const now = new Date();
  const q = Math.ceil((now.getMonth() + 1) / 3);
  return `${now.getFullYear()}-Q${q}`;
}

const DEFAULT_QUOTA_GP = 1_800_000; // fallback until the finance-configured quota loads

export default function FinanceDashboard() {
  const [quarter, setQuarter] = useState(getCurrentQuarter()); // default to current quarter

  const { data: bookingData,  loading: bookLoading }  = useApi(() => getTeamBookings(quarter), [quarter]);
  const { data: quotaData,    loading: quotaLoading } = useApi(() => getQuotas(quarter), [quarter]);
  const { data: shippedData,  loading: shipLoading }  = useApi(() => getTeamShippedByCategory(quarter), [quarter]);
  const { data: pendingData,  loading: pendLoading }  = useApi(() => getPendingAll(), []); // not quarter-filtered

  const loading = bookLoading || quotaLoading || shipLoading || pendLoading;

  // ── Card 1: Closed Won to Goal (booking basis) ──
  const bookedGP  = bookingData?.teamGP ?? 0;
  const goalGP    = quotaData?.teamViewQuotaGp ?? DEFAULT_QUOTA_GP;
  const goalPct   = goalGP > 0 ? (bookedGP / goalGP) * 100 : 0;
  const remaining = Math.max(0, goalGP - bookedGP);

  // ── Card 2: Shipped GP by category (fulfillment basis) ──
  const shipGp   = shippedData?.gp || { hwSw: 0, services: 0, msa: 0, renewalMsa: 0, fod: 0 };
  const shipTotal = shippedData?.totalGP ?? 0;

  // ── Card 3: Pending shipment — all reps ──
  const pendingRows   = pendingData?.byRep || [];
  const totalPendingGP = pendingData?.totalPendingGP ?? 0;
  const totalOpps      = pendingData?.totalOpps ?? 0;

  if (loading) return <DashboardSkeleton />;

  return (
    <div className="page-enter page-wrap" style={{ maxWidth: '1200px' }}>

      {/* Header */}
      <div className="page-header">
        <div>
          <div className="label" style={{ marginBottom: '6px' }}>{quarter} · Company Overview</div>
          <h1 style={{ fontSize: '1.6rem' }}>Finance Dashboard</h1>
        </div>
        <select
          value={quarter}
          onChange={e => setQuarter(e.target.value)}
          className="input"
          style={{ width: 'auto', minWidth: '140px' }}
        >
          {generateQuarterOptions().map(q => (
            <option key={q} value={q}>{q}</option>
          ))}
        </select>
      </div>

      {/* ── Card 1: Closed Won to Goal ── */}
      <ClosedWonToGoal
        bookedGP={bookedGP}
        goalGP={goalGP}
        goalPct={goalPct}
        remaining={remaining}
        isDefaultQuota={quotaData?.isDefault}
      />

      {/* ── Card 2: Shipped GP by Category ── */}
      <ShippedByCategory gp={shipGp} total={shipTotal} />

      {/* ── Card 3: Pending Shipment — All Reps ── */}
      <PendingAllReps rows={pendingRows} totalPendingGP={totalPendingGP} totalOpps={totalOpps} />

      {/* Footnote */}
      <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '18px', lineHeight: 1.5 }}>
        Closed Won to Goal measures <strong>booked</strong> GP (closed-won opportunities by actual close date in {quarter})
        against the {fmt0(goalGP)} Team View quota. Shipped GP by Category is on a <strong>fulfillment</strong> basis
        (shipped in {quarter}), so the two totals will not match. Pending Shipment shows all currently held-back
        opportunities across reps and is not quarter-filtered.
      </p>
    </div>
  );
}

// ─── Card 1: Closed Won to Goal ─────────────────────────────────────────────────
function ClosedWonToGoal({ bookedGP, goalGP, goalPct, remaining, isDefaultQuota }) {
  const barPct   = Math.min(100, goalPct);
  const atGoal   = goalPct >= 100;
  const barColor = atGoal ? 'var(--green)' : 'var(--blue-primary)';

  return (
    <div className="card" style={{ padding: '28px', marginBottom: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <div className="label" style={{ marginBottom: '6px' }}>Closed Won to Goal (Quarter)</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
            <span style={{ fontSize: '2.2rem', fontWeight: 700, fontFamily: 'var(--font-data)', color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
              {fmt0(bookedGP)}
            </span>
            <span style={{ fontSize: '1rem', color: 'var(--text-muted)', fontFamily: 'var(--font-data)' }}>
              of {fmt0(goalGP)} goal
            </span>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="label" style={{ marginBottom: '4px' }}>% to Goal</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 700, fontFamily: 'var(--font-data)', color: barColor }}>
            {pct(goalPct)}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ height: '14px', background: 'var(--bg-elevated)', borderRadius: '999px', overflow: 'hidden', border: '1px solid var(--border)' }}>
        <div style={{
          width: `${barPct}%`, height: '100%',
          background: barColor,
          borderRadius: '999px',
          transition: 'width 0.4s ease',
        }} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', fontSize: '13px', color: 'var(--text-muted)', fontFamily: 'var(--font-data)' }}>
        <span>{atGoal ? 'Goal reached 🎉' : `${fmt0(remaining)} remaining`}</span>
        <span>{isDefaultQuota ? 'default quota' : 'Finance-set quota'}</span>
      </div>
    </div>
  );
}

// ─── Card 2: Shipped GP by Category ─────────────────────────────────────────────
function ShippedByCategory({ gp, total }) {
  const rows = [
    { label: 'HW/SW',              value: gp.hwSw },
    { label: 'Professional Svcs',  value: gp.services },
    { label: 'Managed Svcs (MSA)', value: gp.msa },
    { label: 'Renewal MSA',        value: gp.renewalMsa },
    { label: 'FOD',                value: gp.fod },
  ];
  const maxVal = Math.max(1, ...rows.map(r => r.value || 0)); // avoid /0 for bar widths

  return (
    <div className="card" style={{ overflow: 'hidden', marginBottom: '24px' }}>
      <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h3>Shipped GP by Category (Quarter)</h3>
        <span style={{ fontFamily: 'var(--font-data)', fontWeight: 700, color: 'var(--green)' }}>{fmt(total)}</span>
      </div>
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {rows.map(r => (
          <div key={r.label}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '13px' }}>
              <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{r.label}</span>
              <span className="td-number" style={{ color: 'var(--text-secondary)' }}>
                {fmt(r.value)}
                <span style={{ color: 'var(--text-muted)', marginLeft: '8px' }}>
                  {total > 0 ? pct(((r.value || 0) / total) * 100) : '—'}
                </span>
              </span>
            </div>
            <div style={{ height: '8px', background: 'var(--bg-elevated)', borderRadius: '999px', overflow: 'hidden' }}>
              <div style={{
                width: `${((r.value || 0) / maxVal) * 100}%`, height: '100%',
                background: 'var(--blue-primary)', borderRadius: '999px',
                transition: 'width 0.4s ease',
              }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Card 3: Pending Shipment — All Reps ────────────────────────────────────────
function PendingAllReps({ rows, totalPendingGP, totalOpps }) {
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div className="label" style={{ marginBottom: '6px' }}>Pending Shipment — All Reps</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 700, fontFamily: 'var(--font-data)', color: 'var(--amber)', letterSpacing: '-0.02em' }}>
            {fmt0(totalPendingGP)}
          </div>
        </div>
        <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontFamily: 'var(--font-data)' }}>
          {totalOpps} opp{totalOpps === 1 ? '' : 's'} held back
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Rep</th>
              <th style={{ textAlign: 'right' }}># Opps</th>
              <th style={{ textAlign: 'right' }}>Pending GP</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={3} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '28px' }}>
                  No pending shipments — everything is fulfilled.
                </td>
              </tr>
            ) : rows.map(r => (
              <tr key={r.repId}>
                <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{r.repName || `Rep ${r.repId}`}</td>
                <td className="td-number" style={{ color: 'var(--text-muted)' }}>{r.oppCount}</td>
                <td className="td-number">{fmt(r.pendingGp)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Skeleton / helpers ─────────────────────────────────────────────────────────
function DashboardSkeleton() {
  return (
    <div className="page-wrap" style={{ maxWidth: '1200px' }}>
      <div className="skeleton" style={{ height: '24px', width: '220px', marginBottom: '8px' }} />
      <div className="skeleton" style={{ height: '36px', width: '300px', marginBottom: '32px' }} />
      <div className="skeleton" style={{ height: '150px', borderRadius: '16px', marginBottom: '24px' }} />
      <div className="skeleton" style={{ height: '260px', borderRadius: '16px', marginBottom: '24px' }} />
      <div className="skeleton" style={{ height: '240px', borderRadius: '16px' }} />
    </div>
  );
}

function generateQuarterOptions() {
  const opts = [];
  const now = new Date();
  for (let y = now.getFullYear(); y >= now.getFullYear() - 1; y--) {
    for (let q = 4; q >= 1; q--) {
      opts.push(`${y}-Q${q}`);
    }
  }
  return opts;
}
